import React, { FormEvent, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppEditableForm } from './AppEditableForm'
import { RootState } from 'app/state/types'
import { createNewApp } from 'app/services/client'
import { clearNewApp, setIsLoading, updateNewApp } from 'app/state/appSlice'
import { APP_ROUTES } from 'app/components/AppRouter/routes'
import {
    isTokenExpired,
    onInputChange,
    onSelectChange,
    onFileInputChange,
} from 'app/utils/hooks'
import { readFileBlob } from 'app/utils/fileUtils'
import {
    makeTokenHeaderConfig,
    useTokenHeaderConfig,
} from 'app/services/tokenConfig'
import { handleServerAPIError } from 'app/services/utils'

export const AddForm = () => {
    const { newApp } = useSelector((state: RootState) => state.appSlice)
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [token] = useTokenHeaderConfig()

    const onFormSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()

            if (newApp) {
                dispatch(setIsLoading(true))
                createNewApp(newApp, makeTokenHeaderConfig(token))
                    .then(() => {
                        clearNewApp()
                        navigate(APP_ROUTES.VIEW_APPS_EDITOR)
                    })
                    .catch(e => handleServerAPIError(e, dispatch, navigate))
                    .finally(() => dispatch(setIsLoading(false)))
            } else {
                console.warn('Object NewApp is empty')
            }
        },
        [
            clearNewApp,
            navigate,
            newApp,
            readFileBlob,
            setIsLoading,
            handleServerAPIError,
            dispatch,
        ],
    )

    const onAppChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) =>
            onInputChange(e)(dispatch, updateNewApp),
        [updateNewApp, onInputChange, dispatch],
    )

    const onAppFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) =>
            onFileInputChange(e)(dispatch, updateNewApp),
        [updateNewApp, onFileInputChange, dispatch],
    )

    const onAppSelectChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) =>
            onSelectChange(e)(dispatch, updateNewApp),
        [updateNewApp, onSelectChange, dispatch],
    )

    useEffect(() => {
        if (isTokenExpired()) navigate(APP_ROUTES.LOGIN)
    }, [])

    return (
        <AppEditableForm
            app={newApp}
            onSubmit={onFormSubmit}
            onAppChange={onAppChange}
            onAppSelectChange={onAppSelectChange}
            onAppFileChange={onAppFileChange}
        />
    )
}
